'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { GlobalDOMAttributes } from '@react-types/shared';
import type { SwitchFieldProps as AriaSwitchFieldProps } from 'react-aria-components';
import { SwitchButton as AriaSwitchButton, SwitchField as AriaSwitchField } from 'react-aria-components';
import { Field } from '../field';
import { cx } from '../../../utils/cx';
import { useAnimationEnabledAfterMount } from '../../../utils/hooks/useAnimationEnabledAfterMount';
import { usePrevious } from '../../../utils/usePrevious';
import { ToggleGroup } from './ToggleGroup';

const SEMANTIC_NAME_BASE = 'RomaToggleBase';
const SEMANTIC_NAME = 'RomaToggle';

/**
 * Delays toggle transitions after mounting so selected state is painted in place.
 */
const useToggleAnimationState = (isDisabled: boolean): boolean => {
    const isAnimationEnabledAfterMount = useAnimationEnabledAfterMount();
    const wasDisabled = usePrevious(isDisabled, isDisabled);

    // Availability can resolve asynchronously, changing a selected toggle from disabled to enabled.
    // That state update is not a user action and must not animate the thumb.
    return !(wasDisabled && !isDisabled) && isAnimationEnabledAfterMount;
};

export type ToggleBaseProps = {
    size?: 'xs' | 'sm' | 'md';
    className?: string;
    isHovered?: boolean;
    isFocusVisible?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
};

export const ToggleBase = ({
    className,
    isHovered,
    isDisabled,
    isFocusVisible,
    isSelected,
    size = 'sm',
}: ToggleBaseProps) => {
    const isAnimationEnabled = useToggleAnimationState(Boolean(isDisabled));
    const sizes = {
        xs: {
            root: 'h-4 w-7 p-0.5',
            switch: cx('size-3', isSelected && 'translate-x-3'),
        },
        sm: {
            root: 'h-5 w-9 p-0.5',
            switch: cx('size-4', isSelected && 'translate-x-4'),
        },
        md: {
            root: 'h-6 w-11 p-0.5',
            switch: cx('size-5', isSelected && 'translate-x-5'),
        },
    };

    return (
        <div
            className={cx(
                SEMANTIC_NAME_BASE,
                'shrink-0 cursor-pointer rounded-full bg-tertiary',
                isAnimationEnabled ? 'transition duration-160 ease-linear' : 'transition-none',
                isSelected && 'bg-success-solid',
                isSelected && isHovered && 'bg-success-700',
                isDisabled && 'cursor-default bg-disabled',
                isFocusVisible && 'outline-focus-ring outline-2 -outline-offset-1',
                sizes[size].root,
                className,
            )}
        >
            <div
                className={cx(
                    'rounded-full bg-fg-white shadow-sm',
                    isAnimationEnabled ? 'transition-transform duration-160 ease-in-out' : 'transition-none',
                    isDisabled && 'bg-toggle-button-fg_disabled',
                    sizes[size].switch,
                )}
            />
        </div>
    );
};
ToggleBase.displayName = 'RomaToggleBase';

export type ToggleProps = Omit<
    AriaSwitchFieldProps,
    'children' | 'className' | 'style' | 'render' | keyof GlobalDOMAttributes
> & {
    size?: 'xs' | 'sm' | 'md';
    toggleSize?: 'xs' | 'sm' | 'md';
    label?: string;
    hint?: ReactNode;
    align?: 'left' | 'right';
    /** Applied to the clickable label row. */
    className?: string;
};

const styles = {
    sizes: {
        xs: {
            field: 'gap-0',
            button: 'gap-2',
            label: 'text-sm',
            hint: 'text-sm',
            toggleBase: 'mt-0.5',
        },
        sm: {
            field: 'gap-0',
            button: 'gap-2',
            label: 'text-md',
            hint: 'text-sm',
            toggleBase: '',
        },
        md: {
            field: 'gap-0.5',
            button: 'gap-3',
            label: 'text-display-xs',
            hint: 'text-md',
            toggleBase: '',
        },
    },
    align: {
        left: '',
        right: 'flex-row-reverse justify-between',
    },
    // Indents the hint so it sits under the label text for left-aligned toggles:
    // switch width (from toggleSize) plus the row gap (from size).
    hintIndent: {
        xs: { xs: 'pl-9', sm: 'pl-11', md: 'pl-13' },
        sm: { xs: 'pl-9', sm: 'pl-11', md: 'pl-13' },
        md: { xs: 'pl-10', sm: 'pl-12', md: 'pl-14' },
    },
};

const ToggleRoot = ({
    label,
    hint,
    className,
    toggleSize,
    size = 'sm',
    align = 'right',
    ...fieldProps
}: ToggleProps) => {
    return (
        <Field as={AriaSwitchField} {...fieldProps} className={cx('h-auto items-stretch', styles.sizes[size].field)}>
            <AriaSwitchButton
                className={({ isDisabled }) =>
                    cx(
                        SEMANTIC_NAME,
                        'flex items-start',
                        isDisabled && 'cursor-default',
                        styles.sizes[size].button,
                        styles.align[align],
                        className,
                    )
                }
            >
                {({ isSelected, isDisabled, isFocusVisible, isHovered }) => (
                    <>
                        <ToggleBase
                            size={toggleSize ?? size}
                            isHovered={isHovered}
                            isDisabled={isDisabled}
                            isFocusVisible={isFocusVisible}
                            isSelected={isSelected}
                            className={styles.sizes[size].toggleBase}
                        />

                        {label && (
                            <Field.LabelText
                                className={cx(
                                    // toggle labels have always inherited their colour rather than using text-secondary
                                    'font-semibold text-inherit',
                                    styles.sizes[size].label,
                                    // when inside a toggle group, follow the "hint" styles if no hint is provided
                                    !hint && 'in-data-toggle-group:text-tertiary in-data-toggle-group:font-normal',
                                )}
                            >
                                {label}
                            </Field.LabelText>
                        )}
                    </>
                )}
            </AriaSwitchButton>
            {hint && (
                <Field.HintText
                    className={cx(
                        'font-normal',
                        styles.sizes[size].hint,
                        align === 'left' && styles.hintIndent[size][toggleSize ?? size],
                    )}
                >
                    {hint}
                </Field.HintText>
            )}
        </Field>
    );
};

ToggleRoot.displayName = 'RomaToggle';

export const Toggle: typeof ToggleRoot & { Base: typeof ToggleBase; Group: typeof ToggleGroup } = Object.assign(
    ToggleRoot,
    {
        Base: ToggleBase,
        Group: ToggleGroup,
    },
);
